/**
 * `DataSourcesEditor` — the SQL tab body inside the /reports/new chat's
 * preview pane. Renders the multi-step data-sources stepper: header row
 * with an Add button, then one <DataSourceBlock /> per step with
 * reorder/delete controls.
 *
 * Owns no state — the chat's `useReportAuthoringChat` hook holds the
 * `dataSources` array and passes it in via `blocks`. All mutations go
 * back out through the callback props.
 *
 * `readOnly` toggles every input inside the section; used before the
 * report has been persisted (there's nothing to save-back to yet).
 */
import { Plus } from "lucide-react";
import { Button } from "@flint/ui";
import { DataSourceBlock, type DataSource } from "./data-source-block.tsx";

export type { DataSource } from "./data-source-block.tsx";

interface DataSourcesEditorProps {
  blocks: DataSource[];
  readOnly?: boolean;
  onAdd: () => void;
  onPatch: (index: number, patch: Partial<DataSource>) => void;
  onMove: (index: number, direction: -1 | 1) => void;
  onRemove: (index: number) => void;
}

export function DataSourcesEditor({
  blocks,
  readOnly = false,
  onAdd,
  onPatch,
  onMove,
  onRemove,
}: DataSourcesEditorProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-ink">Data sources</h2>
          <p className="mt-0.5 text-xs text-muted">
            Each block is one SQL query. Steps run in order; a later step can reference earlier
            results by their key — e.g.{" "}
            <code className="rounded-sm bg-paper-2 px-1 py-0.5 font-mono text-xs text-muted">
              {"{{ rows.result }}"}
            </code>
            .
          </p>
        </div>
        <Button
          type="button"
          intent="ghost"
          size="sm"
          onClick={onAdd}
          disabled={readOnly}
          className="shrink-0"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
          Add data source
        </Button>
      </div>

      {blocks.length === 0 ? (
        <div className="mt-6 flex flex-col items-center rounded-sm border border-dashed border-line py-10 text-center">
          <p className="text-sm font-medium text-ink-2">No data source added.</p>
          <p className="mt-1 text-xs text-muted">A report needs at least one.</p>
          <Button
            type="button"
            intent="primary"
            size="sm"
            onClick={onAdd}
            disabled={readOnly}
            className="mt-4"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
            Add data source
          </Button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {blocks.map((block, i) => (
            <DataSourceBlock
              key={i}
              block={block}
              index={i}
              isFirst={i === 0}
              isLast={i === blocks.length - 1}
              readOnly={readOnly}
              onChange={(p) => onPatch(i, p)}
              onMove={(dir) => onMove(i, dir)}
              onRemove={() => onRemove(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
